import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { PseoPage } from "@/components/laneguide/PseoPage";
import { formatKr, lendersForAmount } from "@/lib/pseo";
import { LOAN_PURPOSES, getPurpose } from "@/lib/pseo-content";

const BASE = "https://svensklaneguide.se";

export const Route = createFileRoute("/lan-till/$purpose")({
  loader: ({ params }) => {
    const p = getPurpose(params.purpose);
    if (!p) throw notFound();
    return { slug: p.slug };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData ? getPurpose(loaderData.slug) : null;
    if (!p) {
      return { meta: [{ title: "Sidan hittades inte" }, { name: "robots", content: "noindex" }] };
    }
    const url = `${BASE}/lan-till/${params.purpose}`;
    return {
      meta: [
        { title: `${p.title} | Svensk Låneguide` },
        { name: "description", content: p.description },
        { property: "og:title", content: p.title },
        { property: "og:description", content: p.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "robots", content: "index, follow" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: p.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: BASE },
              { "@type": "ListItem", position: 2, name: "Privatlån", item: `${BASE}/privatlan` },
              { "@type": "ListItem", position: 3, name: p.h1, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: PurposePage,
});

function PurposePage() {
  const { slug } = Route.useLoaderData();
  const p = getPurpose(slug)!;
  const [amount, setAmount] = useState(p.amount);
  const lenders = lendersForAmount(amount);

  return (
    <PseoPage
      h1={p.h1}
      intro={p.intro}
      amount={amount}
      onAmountChange={setAmount}
      amountEditable
      lenders={lenders}
      crumbs={[{ label: "Hem", to: "/" }, { label: "Privatlån", to: "/privatlan" }, { label: `Lån till ${p.name}` }]}
      faq={p.faq}
      epi="pseo-lan-till"
      relatedTitle="Lån till andra ändamål"
      related={[
        ...LOAN_PURPOSES.filter((o) => o.slug !== p.slug)
          .slice(0, 8)
          .map((o) => ({ label: `Lån till ${o.name}`, to: `/lan-till/${o.slug}` })),
        { label: `Alla lån på ${formatKr(p.amount)}`, to: `/lan/${p.amount}-kr` },
      ]}
      extraContent={
        <section className="mx-auto max-w-3xl px-4 pb-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-black text-foreground">
            Så väljer du rätt lån till {p.name}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Jämför alltid effektiv ränta i stället för nominell – den effektiva räntan inkluderar uppläggningsavgift och
            aviavgifter och visar vad lånet faktiskt kostar. Ansöker du via en förmedlare får du flera bud på en enda
            kreditupplysning, vilket i praktiken alltid ger lägre ränta än att söka hos en bank i taget.
          </p>
          <h3 className="mt-6 font-display text-lg font-bold text-foreground">Räkna innan du ansöker</h3>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Använd vår{" "}
            <Link to="/guider/lanekalkyl" className="text-primary underline underline-offset-2">
              lånekalkylator
            </Link>{" "}
            för att se totalkostnaden över hela löptiden, och gör{" "}
            <Link to="/lanetest" className="text-primary underline underline-offset-2">
              lånetestet
            </Link>{" "}
            om du vill ha en rekommendation baserad på din situation.
          </p>
        </section>
      }
    />
  );
}
